import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { errorMessage, toastError, toastSuccess } from '../../../lib/toast';
import { fetchTopOrder, saveTopOrder } from '../../orgChart/orgChartApi';
import '../../orgChart/orgChart.css';

/**
 * One draggable job row.
 *
 * The whole row is the drag handle rather than a separate grip, because the
 * rows are short and a grip would be a small target for what is the only
 * interaction here. The arrow buttons alongside are not a decoration: they are
 * the keyboard- and touch-accessible path to the same reordering, and they stay
 * out of the drag listeners so a click on them is never swallowed as a drag.
 */
function SortableJobRow({ job, index, total, onMove }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: job.job_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`oc-order__row ${isDragging ? 'is-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <span className="oc-order__pos" aria-hidden="true">{index + 1}</span>
      <span className="oc-order__body">
        <span className="oc-order__name">{job.name}</span>
        {job.department && <span className="oc-order__dept">{job.department}</span>}
      </span>
      {/* Buttons are the accessible equivalent of dragging. Pointer events are
          stopped so the sortable listeners on the row do not treat a click as
          the start of a drag. */}
      <span className="oc-order__moves">
        <button
          type="button"
          className="oc-order__move"
          disabled={index === 0}
          aria-label={`Move ${job.name} up`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onMove(index, index - 1)}
        >
          ↑
        </button>
        <button
          type="button"
          className="oc-order__move"
          disabled={index === total - 1}
          aria-label={`Move ${job.name} down`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onMove(index, index + 1)}
        >
          ↓
        </button>
      </span>
    </li>
  );
}

/**
 * Admin ordering for the chart's SECOND level — the jobs reporting to the root
 * (the C-level under the CEO).
 *
 * Odoo has no notion of sibling order, so without this the level renders in
 * whatever order the job ids happen to fall in, which reads as arbitrary and
 * changes as roles are created. The saved order applies to the tree view, the
 * list view and every export, because all of them build their tree through
 * buildTree, which is where the order is applied.
 *
 * Only this one level is orderable, deliberately: it is the level a reader
 * actually looks at as a sequence, and making the whole ~100-job chart
 * hand-orderable would be a tree editor rather than a setting.
 */
export default function OrgChartTopOrderPanel() {
  const [jobs, setJobs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  // The order as last saved, so the panel can tell whether there is anything to
  // save and offer a discard that does not need a refetch.
  const [savedIds, setSavedIds] = useState([]);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    fetchTopOrder()
      .then((d) => {
        if (!active) return;
        const available = d.available || [];
        setJobs(available);
        // Seed from what is RENDERED, not from d.order: the server returns
        // `available` already in effective order (saved first, then the rest
        // alphabetically), so seeding from it means an admin who saves without
        // touching anything pins exactly what they were looking at, rather than
        // saving a partial list that leaves the unplaced jobs still floating.
        setSavedIds(available.map((j) => j.job_id));
        setLoaded(true);
      })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const currentIds = useMemo(() => jobs.map((j) => j.job_id), [jobs]);
  const dirty = useMemo(
    () => currentIds.length !== savedIds.length
      || currentIds.some((id, i) => id !== savedIds[i]),
    [currentIds, savedIds]
  );

  const move = (from, to) => {
    if (to < 0 || to >= jobs.length) return;
    setJobs((prev) => arrayMove(prev, from, to));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setJobs((prev) => {
      const from = prev.findIndex((j) => j.job_id === active.id);
      const to = prev.findIndex((j) => j.job_id === over.id);
      if (from === -1 || to === -1) return prev;
      return arrayMove(prev, from, to);
    });
  };

  const handleSave = () => {
    setSaving(true);
    saveTopOrder(currentIds.join(','))
      .then(() => {
        if (!mountedRef.current) return;
        setSavedIds(currentIds);
        toastSuccess('Org chart order updated');
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        toastError(errorMessage(err, 'Could not save the org chart order.'));
      })
      .finally(() => { if (mountedRef.current) setSaving(false); });
  };

  const handleReset = () => {
    // Back to alphabetical, which is what an empty setting means. Saved
    // immediately so the button means what it says rather than arming a
    // pending change the admin then has to confirm.
    setSaving(true);
    saveTopOrder('')
      .then(() => {
        if (!mountedRef.current) return;
        const alpha = [...jobs].sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setJobs(alpha);
        setSavedIds(alpha.map((j) => j.job_id));
        toastSuccess('Org chart order reset to alphabetical');
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        toastError(errorMessage(err, 'Could not reset the org chart order.'));
      })
      .finally(() => { if (mountedRef.current) setSaving(false); });
  };

  return (
    <section className="osync-card">
      <div className="osync-card__head">
        <div>
          <h3>Org chart order</h3>
          <p className="osync-card__desc">
            The order these positions appear under the CEO, in both views and every export.
            Odoo does not store a sibling order, so without this they fall in an arbitrary one.
          </p>
        </div>
        {loaded && jobs.length > 0 && (
          <span className="osync-count">{jobs.length} position{jobs.length === 1 ? '' : 's'}</span>
        )}
      </div>

      {failed && (
        <div className="osync-empty">Could not load the org chart order.</div>
      )}

      {!failed && !loaded && <div className="osync-loading">Loading…</div>}

      {loaded && jobs.length === 0 && (
        <div className="osync-empty">
          No second-level positions found. Run a sync, or widen the org chart companies above.
        </div>
      )}

      {loaded && jobs.length > 0 && (
        <>
          <p className="oc-order__hint">
            Drag to reorder, or use the arrows. A position added in Odoo later appears
            at the end until you place it.
          </p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={currentIds} strategy={verticalListSortingStrategy}>
              <ol className="oc-order__list">
                {jobs.map((job, i) => (
                  <SortableJobRow
                    key={job.job_id}
                    job={job}
                    index={i}
                    total={jobs.length}
                    onMove={move}
                  />
                ))}
              </ol>
            </SortableContext>
          </DndContext>

          <div className="osync-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!dirty || saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save order'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={saving}
              onClick={handleReset}
            >
              Reset to alphabetical
            </button>
          </div>
        </>
      )}
    </section>
  );
}
